export const DESKTOP_HOST_CONTRACT_VERSION = 2;

export const DESKTOP_TRIGGER_KEYS = [
  "AltRight",
  "AltLeft",
  "ControlRight",
  "ShiftRight",
  "MetaRight",
] as const;

export type DesktopTriggerKey = (typeof DESKTOP_TRIGGER_KEYS)[number];

type DesktopCapability =
  | "globalShortcut"
  | "textInsertion"
  | "clipboard"
  | "permissions"
  | "statusIndicator"
  | "indicatorActions"
  | "indicatorAppearance"
  | "shortcutEducation"
  | "durableRecording"
  | "launchAtLogin"
  | "checkoutReturn"
  | "desktopAuth"
  | "binaryUpdater";

export type DesktopHostInfo = {
  contractVersion: number;
  appVersion: string;
  platform: "macos" | "windows";
  capabilities: DesktopCapability[];
};

export type DesktopSettings = {
  triggerKey: DesktopTriggerKey;
  showIndicator: boolean;
  launchAtLogin: boolean;
};

export type DesktopPermissionStatus = {
  microphone:
    | "granted"
    | "denied"
    | "not-determined"
    | "restricted"
    | "unknown";
  accessibility: "granted" | "denied" | "unknown";
  systemAudio:
    | "granted"
    | "denied"
    | "not-determined"
    | "restricted"
    | "unknown"
    | "unsupported";
};

type DesktopShortcutEvent = {
  type: "tap" | "cancel";
  at: number;
};
export type DesktopTrayAction =
  | "dictate"
  | "record"
  | "finish"
  | "upload"
  | "pasteLast";
type DesktopTrayLabels = Record<
  "dictate" | "record" | "finish" | "upload" | "open",
  string
> & { pasteLast?: string };

type DesktopDictationSettingsOpenEvent = {
  at: number;
};

type DesktopDictationLanguageSelectEvent = {
  at: number;
  language: string;
};

export type DesktopIndicatorAction =
  | "start"
  | "dictate"
  | "showDictationGuide"
  | "dismissGuide"
  | "cancel"
  | "finish"
  | "pause"
  | "resume"
  | "copy"
  | "rewriteCleanUp"
  | "rewriteSimplify"
  | "showOriginal"
  | "dismiss"
  | "openPaywall";

type DesktopIndicatorActionEvent = {
  at: number;
  action: DesktopIndicatorAction;
};

export type DesktopIndicatorAppearance = "light" | "dark" | "system";

type DesktopIndicator = {
  state:
    | "hidden"
    | "idle"
    | "arming"
    | "listening"
    | "transcribing"
    | "inserting"
    | "review"
    | "success"
    | "error"
    | "paywall";
  level?: number;
  message?: string;
  interfaceDirection?: "ltr" | "rtl";
  dictationLanguage?: string;
  recentDictationLanguages?: string[];
  openLanguagePicker?: boolean;
  languagePrompt?: string;
  allLanguagesLabel?: string;
  cancelLabel?: string;
  finishLabel?: string;
  pauseLabel?: string;
  tryAgainLabel?: string;
  resultTitle?: string;
  resultText?: string;
  copyLabel?: string;
  copiedLabel?: string;
  rewriteVariant?: "original" | "cleanUp" | "simplify";
  rewritePending?: "cleanUp" | "simplify";
  rewriteFailed?: boolean;
  originalLabel?: string;
  cleanUpLabel?: string;
  simplifyLabel?: string;
  rewriteFailedLabel?: string;
  timerMode?: "countdown" | "elapsed";
  recordingPaused?: boolean;
  recordingElapsedMs?: number;
  recordingStartedAt?: number;
  recordingNotice?: string;
  guideTitle?: string;
  guideBody?: string;
  guideActionLabel?: string;
  guideKeycap?: string;
  guideStartLabel?: string;
  guideFinishLabel?: string;
  dismissLabel?: string;
  dictateLabel?: string;
  recordLabel?: string;
  notice?: string;
};

type DesktopInsertionResult = {
  status: "inserted" | "dispatched" | "not-inserted";
  reason?:
    | "protected-app"
    | "paste-unavailable"
    | "empty"
    | "no-target"
    | "target-changed"
    | "unconfirmed";
};

export type DesktopDurableRecordingState =
  | "recording"
  | "ready_to_upload"
  | "uploading"
  | "uploaded"
  | "finalizing"
  | "delivered"
  | "discarded";

export type DesktopDurableRecording = {
  id: string;
  state: DesktopDurableRecordingState;
  fileName: string;
  language: string;
  mimeType: "audio/webm;codecs=opus";
  createdAt: number;
  durationMs: number;
  sizeBytes: number;
  lastSequence: number;
  recovered: boolean;
  limitReached?: boolean;
  downloadUrl?: string;
  transcriptId?: string;
  multipartObjectKey?: string;
  multipartUploadId?: string;
  multipartPartSize?: number;
  multipartPartCount?: number;
  uploadedParts?: number;
};

export type DesktopDurableRecordingUpdate = Pick<
  DesktopDurableRecording,
  "id" | "state"
> &
  Partial<
    Pick<
      DesktopDurableRecording,
      | "downloadUrl"
      | "transcriptId"
      | "multipartObjectKey"
      | "multipartUploadId"
      | "multipartPartSize"
      | "multipartPartCount"
      | "uploadedParts"
    >
  > & { ownerId: string };

export type DesktopUpdaterFailureCode =
  | "signature"
  | "permission"
  | "disk-space"
  | "checksum"
  | "manifest"
  | "network"
  | "unknown";

export type DesktopHostEvent = {
  event:
    | "runtime.clientLoaded"
    | "runtime.clientLoadFailed"
    | "runtime.rendererGone"
    | "runtime.updaterInstalled"
    | "runtime.updaterFailed"
    | "runtime.updaterBlocked"
    | "runtime.globalShortcutUnavailable";
  at: number;
  reason?:
    | "network"
    | "timeout"
    | "render-process-gone"
    | "updater"
    | "native-hook";
  updaterStage?: "check" | "download" | "staging";
  updaterCode?: DesktopUpdaterFailureCode;
};

export type DesktopUpdateStatus = {
  state: "idle" | "checking" | "available" | "downloaded" | "error" | "blocked";
  version?: string;
};

type DesktopHostBridge = {
  showWindow?: () => Promise<void>;
  setTrayLabels?: (labels: DesktopTrayLabels) => Promise<void>;
  onTrayAction?: (callback: (action: unknown) => void) => () => void;
  ready: (contractVersion: number) => Promise<void>;
  getInfo: () => Promise<unknown>;
  getSettings: () => Promise<unknown>;
  updateSettings: (patch: unknown) => Promise<unknown>;
  getPermissions: () => Promise<unknown>;
  requestPermission: (
    permission: "microphone" | "accessibility",
  ) => Promise<unknown>;
  openPermissionSettings: (
    permission: "microphone" | "accessibility" | "systemAudio",
  ) => Promise<void>;
  enableGlobalShortcut: () => Promise<unknown>;
  insertText: (text: string) => Promise<unknown>;
  writeClipboard?: (text: string) => Promise<void>;
  indicatorDirectionVersion?: number;
  durableRecordingVersion?: number;
  bubbleDictationVersion?: number;
  setIndicator: (indicator: DesktopIndicator) => Promise<void>;
  setIndicatorAppearance?: (
    appearance: DesktopIndicatorAppearance,
  ) => Promise<void>;
  startDurableRecording?: (input: unknown) => Promise<unknown>;
  appendDurableRecordingChunk?: (input: unknown) => Promise<unknown>;
  checkpointDurableRecording?: (input: unknown) => Promise<unknown>;
  finishDurableRecording?: (input: unknown) => Promise<unknown>;
  listDurableRecordings?: (input: unknown) => Promise<unknown>;
  updateDurableRecording?: (input: unknown) => Promise<unknown>;
  uploadDurableRecordingRange?: (input: unknown) => Promise<unknown>;
  verifyDurableRecordingRemote?: (input: unknown) => Promise<unknown>;
  completeDurableRecordingDelivery?: (input: unknown) => Promise<unknown>;
  discardDurableRecording?: (input: unknown) => Promise<unknown>;
  showNotification?: (input: unknown) => Promise<unknown>;
  consumeHostEvents: () => Promise<unknown>;
  consumeCheckoutReturns: () => Promise<unknown>;
  consumeDesktopAuthReturns?: () => Promise<unknown>;
  checkForUpdates: () => Promise<void>;
  installUpdate: () => Promise<unknown>;
  getUpdateStatus: () => Promise<unknown>;
  reloadClient: () => Promise<void>;
  onShortcut: (callback: (event: unknown) => void) => () => void;
  onDictationSettingsOpen?: (callback: (event: unknown) => void) => () => void;
  onDictationLanguageSelect?: (
    callback: (event: unknown) => void,
  ) => () => void;
  onIndicatorAction?: (callback: (event: unknown) => void) => () => void;
  onCheckoutReturn: (callback: (event: unknown) => void) => () => void;
  onDesktopAuthReturn?: (callback: (event: unknown) => void) => () => void;
  onHostEvent: (callback: (event: unknown) => void) => () => void;
  onUpdateStatus: (callback: (event: unknown) => void) => () => void;
};

declare global {
  interface Window {
    desktopHost?: DesktopHostBridge;
  }
}

const _isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const _isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const _isTriggerKey = (value: unknown): value is DesktopTriggerKey =>
  typeof value === "string" &&
  (DESKTOP_TRIGGER_KEYS as readonly string[]).includes(value);

const _isCapability = (value: unknown): value is DesktopCapability =>
  typeof value === "string" &&
  [
    "globalShortcut",
    "textInsertion",
    "clipboard",
    "permissions",
    "statusIndicator",
    "indicatorActions",
    "indicatorAppearance",
    "shortcutEducation",
    "durableRecording",
    "launchAtLogin",
    "checkoutReturn",
    "desktopAuth",
    "binaryUpdater",
  ].includes(value);

const _hostInfo = (value: unknown): DesktopHostInfo => {
  if (
    !_isRecord(value) ||
    !_isFiniteNumber(value.contractVersion) ||
    typeof value.appVersion !== "string" ||
    (value.platform !== "macos" && value.platform !== "windows") ||
    !Array.isArray(value.capabilities) ||
    !value.capabilities.every((capability) => typeof capability === "string")
  ) {
    throw new Error("Desktop host returned an invalid capability contract");
  }
  return {
    contractVersion: value.contractVersion,
    appVersion: value.appVersion,
    platform: value.platform,
    capabilities: value.capabilities.filter(_isCapability),
  };
};

const _settings = (value: unknown): DesktopSettings => {
  if (
    !_isRecord(value) ||
    !_isTriggerKey(value.triggerKey) ||
    typeof value.showIndicator !== "boolean" ||
    typeof value.launchAtLogin !== "boolean"
  ) {
    throw new Error("Desktop host returned invalid settings");
  }
  return {
    triggerKey: value.triggerKey,
    showIndicator: value.showIndicator,
    launchAtLogin: value.launchAtLogin,
  };
};

const _permissions = (value: unknown): DesktopPermissionStatus => {
  const microphone = [
    "granted",
    "denied",
    "not-determined",
    "restricted",
    "unknown",
  ];
  const accessibility = ["granted", "denied", "unknown"];
  if (
    !_isRecord(value) ||
    typeof value.microphone !== "string" ||
    !microphone.includes(value.microphone) ||
    typeof value.accessibility !== "string" ||
    !accessibility.includes(value.accessibility)
  ) {
    throw new Error("Desktop host returned invalid permission state");
  }
  return {
    microphone: value.microphone as DesktopPermissionStatus["microphone"],
    systemAudio:
      typeof value.systemAudio === "string" &&
      [...microphone, "unsupported"].includes(value.systemAudio)
        ? (value.systemAudio as DesktopPermissionStatus["systemAudio"])
        : "unknown",
    accessibility:
      value.accessibility as DesktopPermissionStatus["accessibility"],
  };
};

const _shortcutEvent = (value: unknown): DesktopShortcutEvent | undefined => {
  if (
    !_isRecord(value) ||
    (value.type !== "tap" && value.type !== "cancel") ||
    !_isFiniteNumber(value.at)
  ) {
    return undefined;
  }
  return { type: value.type, at: value.at };
};

const _dictationSettingsOpenEvent = (
  value: unknown,
): DesktopDictationSettingsOpenEvent | undefined => {
  if (!_isRecord(value) || !_isFiniteNumber(value.at)) return undefined;
  return { at: value.at };
};

const _dictationLanguageSelectEvent = (
  value: unknown,
): DesktopDictationLanguageSelectEvent | undefined => {
  if (
    !_isRecord(value) ||
    !_isFiniteNumber(value.at) ||
    typeof value.language !== "string" ||
    !/^[a-z]{2}$/.test(value.language)
  ) {
    return undefined;
  }
  return { at: value.at, language: value.language };
};

const _indicatorActionEvent = (
  value: unknown,
): DesktopIndicatorActionEvent | undefined => {
  const actions: DesktopIndicatorAction[] = [
    "start",
    "dictate",
    "showDictationGuide",
    "dismissGuide",
    "cancel",
    "finish",
    "pause",
    "resume",
    "copy",
    "rewriteCleanUp",
    "rewriteSimplify",
    "showOriginal",
    "dismiss",
    "openPaywall",
  ];
  if (
    !_isRecord(value) ||
    Object.keys(value).some((key) => key !== "at" && key !== "action") ||
    !_isFiniteNumber(value.at) ||
    typeof value.action !== "string" ||
    !actions.includes(value.action as DesktopIndicatorAction)
  ) {
    return undefined;
  }
  return { at: value.at, action: value.action as DesktopIndicatorAction };
};

const _insertionResult = (value: unknown): DesktopInsertionResult => {
  if (
    !_isRecord(value) ||
    (value.status !== "inserted" &&
      value.status !== "dispatched" &&
      value.status !== "not-inserted")
  ) {
    throw new Error("Desktop host returned an invalid insertion result");
  }
  const reason =
    value.reason === "protected-app" ||
    value.reason === "paste-unavailable" ||
    value.reason === "empty" ||
    value.reason === "no-target" ||
    value.reason === "target-changed" ||
    value.reason === "unconfirmed"
      ? value.reason
      : undefined;
  return { status: value.status, ...(reason ? { reason } : {}) };
};

const _recordingStates: DesktopDurableRecordingState[] = [
  "recording",
  "ready_to_upload",
  "uploading",
  "uploaded",
  "finalizing",
  "delivered",
  "discarded",
];

const _optionalString = (
  value: unknown,
  maximumLength: number,
): value is string | undefined =>
  value === undefined ||
  (typeof value === "string" &&
    value.length > 0 &&
    value.length <= maximumLength);

const _durableRecording = (value: unknown): DesktopDurableRecording => {
  if (
    !_isRecord(value) ||
    typeof value.id !== "string" ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value.id,
    ) ||
    typeof value.state !== "string" ||
    !_recordingStates.includes(value.state as DesktopDurableRecordingState) ||
    typeof value.fileName !== "string" ||
    value.fileName.length === 0 ||
    value.fileName.length > 180 ||
    typeof value.language !== "string" ||
    !/^[a-z]{2}$/.test(value.language) ||
    value.mimeType !== "audio/webm;codecs=opus" ||
    !_isFiniteNumber(value.createdAt) ||
    !_isFiniteNumber(value.durationMs) ||
    !_isFiniteNumber(value.sizeBytes) ||
    !_isFiniteNumber(value.lastSequence) ||
    typeof value.recovered !== "boolean" ||
    (value.limitReached !== undefined &&
      typeof value.limitReached !== "boolean") ||
    !_optionalString(value.downloadUrl, 4_096) ||
    !_optionalString(value.transcriptId, 200) ||
    !_optionalString(value.multipartObjectKey, 1_024) ||
    !_optionalString(value.multipartUploadId, 1_024) ||
    (value.multipartPartSize !== undefined &&
      !_isFiniteNumber(value.multipartPartSize)) ||
    (value.multipartPartCount !== undefined &&
      !_isFiniteNumber(value.multipartPartCount)) ||
    (value.uploadedParts !== undefined && !_isFiniteNumber(value.uploadedParts))
  ) {
    throw new Error("Desktop host returned an invalid durable recording");
  }
  return value as DesktopDurableRecording;
};

const _recordingAppendResult = (
  value: unknown,
): { persistedSequence: number; sizeBytes: number } => {
  if (
    !_isRecord(value) ||
    !_isFiniteNumber(value.persistedSequence) ||
    !_isFiniteNumber(value.sizeBytes)
  ) {
    throw new Error("Desktop host returned an invalid recording write result");
  }
  return {
    persistedSequence: value.persistedSequence,
    sizeBytes: value.sizeBytes,
  };
};

const _hostEvent = (value: unknown): DesktopHostEvent | undefined => {
  if (!_isRecord(value) || !_isFiniteNumber(value.at)) return undefined;
  const events: DesktopHostEvent["event"][] = [
    "runtime.clientLoaded",
    "runtime.clientLoadFailed",
    "runtime.rendererGone",
    "runtime.updaterInstalled",
    "runtime.updaterFailed",
    "runtime.updaterBlocked",
    "runtime.globalShortcutUnavailable",
  ];
  if (
    typeof value.event !== "string" ||
    !events.includes(value.event as DesktopHostEvent["event"])
  ) {
    return undefined;
  }
  const reasons: NonNullable<DesktopHostEvent["reason"]>[] = [
    "network",
    "timeout",
    "render-process-gone",
    "updater",
    "native-hook",
  ];
  const reason =
    typeof value.reason === "string" &&
    reasons.includes(value.reason as NonNullable<DesktopHostEvent["reason"]>)
      ? (value.reason as NonNullable<DesktopHostEvent["reason"]>)
      : undefined;
  const isUpdaterEvent =
    value.event === "runtime.updaterFailed" ||
    value.event === "runtime.updaterBlocked";
  const updaterStages: NonNullable<DesktopHostEvent["updaterStage"]>[] = [
    "check",
    "download",
    "staging",
  ];
  const updaterStage =
    isUpdaterEvent &&
    typeof value.updaterStage === "string" &&
    updaterStages.includes(
      value.updaterStage as NonNullable<DesktopHostEvent["updaterStage"]>,
    )
      ? (value.updaterStage as NonNullable<DesktopHostEvent["updaterStage"]>)
      : undefined;
  const updaterCodes: DesktopUpdaterFailureCode[] = [
    "signature",
    "permission",
    "disk-space",
    "checksum",
    "manifest",
    "network",
    "unknown",
  ];
  const updaterCode =
    isUpdaterEvent &&
    typeof value.updaterCode === "string" &&
    updaterCodes.includes(value.updaterCode as DesktopUpdaterFailureCode)
      ? (value.updaterCode as DesktopUpdaterFailureCode)
      : undefined;
  return {
    event: value.event as DesktopHostEvent["event"],
    at: value.at,
    ...(reason ? { reason } : {}),
    ...(updaterStage ? { updaterStage } : {}),
    ...(updaterCode ? { updaterCode } : {}),
  };
};

const _updateStatus = (value: unknown): DesktopUpdateStatus | undefined => {
  if (!_isRecord(value)) return undefined;
  const states: DesktopUpdateStatus["state"][] = [
    "idle",
    "checking",
    "available",
    "downloaded",
    "error",
    "blocked",
  ];
  if (
    typeof value.state !== "string" ||
    !states.includes(value.state as DesktopUpdateStatus["state"])
  ) {
    return undefined;
  }
  return {
    state: value.state as DesktopUpdateStatus["state"],
    ...(typeof value.version === "string" ? { version: value.version } : {}),
  };
};

const _indicatorForBridge = (
  bridge: DesktopHostBridge,
  indicator: DesktopIndicator,
): DesktopIndicator => {
  if (bridge.indicatorDirectionVersion === 1) return indicator;
  const compatible = { ...indicator };
  delete compatible.interfaceDirection;
  return compatible;
};

export const createDesktopClient = (
  bridge: DesktopHostBridge | undefined = typeof window === "undefined"
    ? undefined
    : window.desktopHost,
) => ({
  isAvailable: Boolean(bridge),
  supportsIndicatorAppearance:
    typeof bridge?.setIndicatorAppearance === "function",
  supportsIndicatorLanguage:
    typeof bridge?.onDictationLanguageSelect === "function",
  supportsIndicatorActions: typeof bridge?.onIndicatorAction === "function",
  // The idle pill dictates on click and the tray can paste the last transcript.
  supportsBubbleDictation: bridge?.bubbleDictationVersion === 1,
  supportsDurableRecording:
    bridge?.durableRecordingVersion === 1 &&
    typeof bridge.startDurableRecording === "function" &&
    typeof bridge.appendDurableRecordingChunk === "function" &&
    typeof bridge.checkpointDurableRecording === "function" &&
    typeof bridge.finishDurableRecording === "function" &&
    typeof bridge.listDurableRecordings === "function" &&
    typeof bridge.updateDurableRecording === "function" &&
    typeof bridge.uploadDurableRecordingRange === "function" &&
    typeof bridge.verifyDurableRecordingRemote === "function" &&
    typeof bridge.completeDurableRecordingDelivery === "function" &&
    typeof bridge.discardDurableRecording === "function",
  supportsDesktopAuth:
    typeof bridge?.consumeDesktopAuthReturns === "function" &&
    typeof bridge?.onDesktopAuthReturn === "function",

  ready: async (
    contractVersion = DESKTOP_HOST_CONTRACT_VERSION,
  ): Promise<void> => bridge?.ready(contractVersion),

  getInfo: async (): Promise<DesktopHostInfo | undefined> =>
    bridge ? _hostInfo(await bridge.getInfo()) : undefined,

  getSettings: async (): Promise<DesktopSettings | undefined> =>
    bridge ? _settings(await bridge.getSettings()) : undefined,

  updateSettings: async (
    patch: Partial<DesktopSettings>,
  ): Promise<DesktopSettings | undefined> =>
    bridge ? _settings(await bridge.updateSettings(patch)) : undefined,

  getPermissions: async (): Promise<DesktopPermissionStatus | undefined> =>
    bridge ? _permissions(await bridge.getPermissions()) : undefined,
  showWindow: async (): Promise<void> => bridge?.showWindow?.(),

  setTrayLabels: async (labels: DesktopTrayLabels): Promise<void> =>
    bridge?.setTrayLabels?.(labels),
  onTrayAction: (callback: (action: DesktopTrayAction) => void): (() => void) =>
    bridge?.onTrayAction?.((action) => {
      if (
        action === "dictate" ||
        action === "record" ||
        action === "finish" ||
        action === "upload" ||
        action === "pasteLast"
      )
        callback(action);
    }) ?? (() => undefined),
  consumeDesktopAuthReturns: async (): Promise<string[]> => {
    if (!bridge?.consumeDesktopAuthReturns) return [];
    const value = await bridge.consumeDesktopAuthReturns();
    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === "string")
      : [];
  },
  onDesktopAuthReturn: (callback: (query: string) => void): (() => void) =>
    bridge?.onDesktopAuthReturn?.((value) => {
      if (typeof value === "string") callback(value);
    }) ?? (() => undefined),

  requestPermission: async (
    permission: "microphone" | "accessibility",
  ): Promise<DesktopPermissionStatus | undefined> =>
    bridge
      ? _permissions(await bridge.requestPermission(permission))
      : undefined,

  openPermissionSettings: async (
    permission: "microphone" | "accessibility" | "systemAudio",
  ): Promise<void> => bridge?.openPermissionSettings(permission),

  enableGlobalShortcut: async (): Promise<boolean> =>
    bridge ? (await bridge.enableGlobalShortcut()) === true : false,

  insertText: async (text: string): Promise<DesktopInsertionResult> =>
    bridge
      ? _insertionResult(await bridge.insertText(text))
      : { status: "not-inserted", reason: "paste-unavailable" },

  writeClipboard: async (text: string): Promise<boolean> => {
    if (!bridge?.writeClipboard) return false;
    await bridge.writeClipboard(text);
    return true;
  },

  setIndicator: async (indicator: DesktopIndicator): Promise<void> => {
    if (!bridge) return;
    await bridge.setIndicator(_indicatorForBridge(bridge, indicator));
  },

  setIndicatorAppearance: async (
    appearance: DesktopIndicatorAppearance,
  ): Promise<void> => bridge?.setIndicatorAppearance?.(appearance),

  startDurableRecording: async (input: {
    ownerId: string;
    fileName: string;
    language: string;
    mimeType: "audio/webm;codecs=opus";
    startedAt: number;
  }): Promise<DesktopDurableRecording | undefined> =>
    bridge?.startDurableRecording
      ? _durableRecording(await bridge.startDurableRecording(input))
      : undefined,

  appendDurableRecordingChunk: async (input: {
    id: string;
    ownerId: string;
    sequence: number;
    chunk: ArrayBuffer;
  }): Promise<{ persistedSequence: number; sizeBytes: number } | undefined> =>
    bridge?.appendDurableRecordingChunk
      ? _recordingAppendResult(await bridge.appendDurableRecordingChunk(input))
      : undefined,

  checkpointDurableRecording: async (input: {
    id: string;
    ownerId: string;
    durationMs: number;
    language?: string;
  }): Promise<DesktopDurableRecording | undefined> =>
    bridge?.checkpointDurableRecording
      ? _durableRecording(await bridge.checkpointDurableRecording(input))
      : undefined,

  finishDurableRecording: async (input: {
    id: string;
    ownerId: string;
    durationMs: number;
    language?: string;
    limitReached?: boolean;
  }): Promise<DesktopDurableRecording | undefined> =>
    bridge?.finishDurableRecording
      ? _durableRecording(await bridge.finishDurableRecording(input))
      : undefined,

  listDurableRecordings: async (
    ownerId: string,
  ): Promise<DesktopDurableRecording[]> => {
    if (!bridge?.listDurableRecordings) return [];
    const value = await bridge.listDurableRecordings({ ownerId });
    if (!Array.isArray(value)) {
      throw new Error("Desktop host returned an invalid recording list");
    }
    return value.map(_durableRecording);
  },

  updateDurableRecording: async (
    input: DesktopDurableRecordingUpdate,
  ): Promise<DesktopDurableRecording | undefined> =>
    bridge?.updateDurableRecording
      ? _durableRecording(await bridge.updateDurableRecording(input))
      : undefined,

  uploadDurableRecordingRange: async (input: {
    id: string;
    ownerId: string;
    url: string;
    start: number;
    length: number;
    contentType: "audio/webm;codecs=opus";
  }): Promise<number> => {
    if (!bridge?.uploadDurableRecordingRange) return 0;
    const value = await bridge.uploadDurableRecordingRange(input);
    if (!_isRecord(value) || !_isFiniteNumber(value.bytesUploaded)) {
      throw new Error("Desktop host returned an invalid upload result");
    }
    return value.bytesUploaded;
  },

  verifyDurableRecordingRemote: async (input: {
    id: string;
    ownerId: string;
    url: string;
  }): Promise<boolean> =>
    bridge?.verifyDurableRecordingRemote
      ? (await bridge.verifyDurableRecordingRemote(input)) === true
      : false,

  completeDurableRecordingDelivery: async (input: {
    id: string;
    ownerId: string;
  }): Promise<void> => {
    await bridge?.completeDurableRecordingDelivery?.(input);
  },

  discardDurableRecording: async (input: {
    id: string;
    ownerId: string;
  }): Promise<void> => {
    await bridge?.discardDurableRecording?.(input);
  },

  showNotification: async (input: {
    title: string;
    body: string;
  }): Promise<void> => {
    await bridge?.showNotification?.(input);
  },

  consumeHostEvents: async (): Promise<DesktopHostEvent[]> => {
    if (!bridge) return [];
    const value = await bridge.consumeHostEvents();
    if (!Array.isArray(value)) return [];
    return value.flatMap((event) => {
      const parsed = _hostEvent(event);
      return parsed ? [parsed] : [];
    });
  },

  consumeCheckoutReturns: async (): Promise<string[]> => {
    if (!bridge) return [];
    const value = await bridge.consumeCheckoutReturns();
    return Array.isArray(value)
      ? value.filter((query): query is string => typeof query === "string")
      : [];
  },

  checkForUpdates: async (): Promise<void> => bridge?.checkForUpdates(),

  installUpdate: async (): Promise<boolean> =>
    bridge ? (await bridge.installUpdate()) === true : false,

  getUpdateStatus: async (): Promise<DesktopUpdateStatus | undefined> =>
    bridge ? _updateStatus(await bridge.getUpdateStatus()) : undefined,

  reloadClient: async (): Promise<void> => bridge?.reloadClient(),

  onShortcut: (callback: (event: DesktopShortcutEvent) => void): (() => void) =>
    bridge?.onShortcut((value) => {
      const event = _shortcutEvent(value);
      if (event) callback(event);
    }) ?? (() => undefined),

  onDictationSettingsOpen: (callback: () => void): (() => void) =>
    bridge?.onDictationSettingsOpen?.((value) => {
      if (_dictationSettingsOpenEvent(value)) callback();
    }) ?? (() => undefined),

  onDictationLanguageSelect: (
    callback: (language: string) => void,
  ): (() => void) =>
    bridge?.onDictationLanguageSelect?.((value) => {
      const event = _dictationLanguageSelectEvent(value);
      if (event) callback(event.language);
    }) ?? (() => undefined),

  onIndicatorAction: (
    callback: (action: DesktopIndicatorAction) => void,
  ): (() => void) =>
    bridge?.onIndicatorAction?.((value) => {
      const event = _indicatorActionEvent(value);
      if (event) callback(event.action);
    }) ?? (() => undefined),

  onCheckoutReturn: (callback: (query: string) => void): (() => void) =>
    bridge?.onCheckoutReturn((value) => {
      if (typeof value === "string") callback(value);
    }) ?? (() => undefined),

  onHostEvent: (callback: (event: DesktopHostEvent) => void): (() => void) =>
    bridge?.onHostEvent((value) => {
      const event = _hostEvent(value);
      if (event) callback(event);
    }) ?? (() => undefined),

  onUpdateStatus: (
    callback: (status: DesktopUpdateStatus) => void,
  ): (() => void) =>
    bridge?.onUpdateStatus((value) => {
      const status = _updateStatus(value);
      if (status) callback(status);
    }) ?? (() => undefined),
});

export type DesktopClient = ReturnType<typeof createDesktopClient>;
